// TanStack Query hooks for server-authoritative chat state.
//
// Wraps lib/feedbackApi.ts in useQuery/useMutation, with consistent
// query-key shape so callers can invalidate from anywhere. Server is the
// source of truth for everything in this module; SessionContext layers
// in-memory streaming state on top of the queries' cached values.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFolder,
  deleteChat,
  deleteFolder,
  getChat,
  getFolderCollapse,
  listChats,
  listFolders,
  patchChat,
  patchFolder,
  setChatFolder,
  setFolderCollapse,
  updateAccumulatedGraph,
  type ChatDetail,
  type ChatSummary,
  type FolderRecord,
} from './feedbackApi';

// Single source of truth for query keys. Use `chatKey(id)` for a chat
// detail and `chatsKey()` for the list. Co-locating them makes
// invalidations refactor-safe.
export const chatsKey = () => ['chats'] as const;
export const chatKey = (id: string | null | undefined) => ['chat', id] as const;
export const foldersKey = () => ['folders'] as const;
export const folderCollapseKey = () => ['folder-collapse'] as const;

/** List of chat summaries for the current client_id. */
export function useChats() {
  return useQuery<ChatSummary[]>({
    queryKey: chatsKey(),
    queryFn: listChats,
    staleTime: 10_000,
  });
}

/** Detail (turns, events, accumulated_graph) for a single chat. Disabled
 *  when `id` is null/empty so SessionContext can pass `activeSessionId`
 *  directly without conditional rendering. */
export function useChat(id: string | null | undefined) {
  return useQuery<ChatDetail>({
    queryKey: chatKey(id),
    queryFn: () => getChat(id as string),
    enabled: !!id,
    staleTime: 10_000,
  });
}

/** PATCH title or is_sensitive. Invalidates both the list (title shown in
 *  sidebar) and the open detail. */
export function usePatchChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      chat_id,
      patch,
    }: {
      chat_id: string;
      patch: { title?: string; is_sensitive?: boolean };
    }) => patchChat(chat_id, patch),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: chatsKey() });
      qc.invalidateQueries({ queryKey: chatKey(variables.chat_id) });
    },
  });
}

/** DELETE a chat. Optimistically removes it from the list so the sidebar
 *  doesn't lag behind the click; rolls back on error. */
export function useDeleteChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chat_id: string) => deleteChat(chat_id),
    onMutate: async (chat_id) => {
      await qc.cancelQueries({ queryKey: chatsKey() });
      const prev = qc.getQueryData<ChatSummary[]>(chatsKey());
      if (prev) {
        qc.setQueryData<ChatSummary[]>(
          chatsKey(),
          prev.filter((c) => c.chat_id !== chat_id),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(chatsKey(), ctx.prev);
    },
    onSettled: (_data, _err, chat_id) => {
      qc.invalidateQueries({ queryKey: chatsKey() });
      qc.removeQueries({ queryKey: chatKey(chat_id) });
    },
  });
}

/** Folder list. */
export function useFolders() {
  return useQuery<FolderRecord[]>({
    queryKey: foldersKey(),
    queryFn: listFolders,
    staleTime: 30_000,
  });
}

/** Folder collapse state (per client_id). */
export function useFolderCollapse() {
  return useQuery<Record<string, boolean>>({
    queryKey: folderCollapseKey(),
    queryFn: getFolderCollapse,
    staleTime: 30_000,
  });
}

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ folder_id, name }: { folder_id: string; name: string }) =>
      createFolder({ folder_id, name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: foldersKey() }),
  });
}

export function usePatchFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ folder_id, name }: { folder_id: string; name: string }) =>
      patchFolder(folder_id, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: foldersKey() }),
  });
}

export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (folder_id: string) => deleteFolder(folder_id),
    onSuccess: () => {
      // Folder delete also clears folder_id on member chats → list changes.
      qc.invalidateQueries({ queryKey: foldersKey() });
      qc.invalidateQueries({ queryKey: chatsKey() });
    },
  });
}

export function useSetChatFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      chat_id,
      folder_id,
    }: {
      chat_id: string;
      folder_id: string | null;
    }) => setChatFolder(chat_id, folder_id),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: chatsKey() });
      qc.invalidateQueries({ queryKey: chatKey(variables.chat_id) });
    },
  });
}

export function useSetFolderCollapse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ folder_id, collapsed }: { folder_id: string; collapsed: boolean }) =>
      setFolderCollapse(folder_id, collapsed),
    onSuccess: () => qc.invalidateQueries({ queryKey: folderCollapseKey() }),
  });
}

// ── Pinned chats ──────────────────────────────────────────────────────
//
// Pins live in the per-client folder-collapse key/value store under a
// `pin:<chat_id>` key (value true = pinned). The server accepts unknown
// keys by design (routes/folders.py), so this needs no backend change or
// migration, persists across devices like folders do, and older clients
// simply ignore the extra keys. Folder ids are UUIDs, so the prefix can't
// collide with a real folder.
export const PIN_KEY_PREFIX = 'pin:';

export function pinKey(chatId: string): string {
  return `${PIN_KEY_PREFIX}${chatId}`;
}

/** Pin/unpin a chat. Optimistic so the sidebar regroups immediately. */
export function useSetChatPinned() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ chat_id, pinned }: { chat_id: string; pinned: boolean }) =>
      setFolderCollapse(pinKey(chat_id), pinned),
    onMutate: async ({ chat_id, pinned }) => {
      await qc.cancelQueries({ queryKey: folderCollapseKey() });
      const prev = qc.getQueryData<Record<string, boolean>>(folderCollapseKey());
      qc.setQueryData<Record<string, boolean>>(folderCollapseKey(), {
        ...(prev ?? {}),
        [pinKey(chat_id)]: pinned,
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(folderCollapseKey(), ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: folderCollapseKey() }),
  });
}

/** Full-replace last-write-wins PATCH of the per-session accumulated graph.
 *  No optimistic update — the SPA already shows in-memory state; this is
 *  pure persistence. Invalidates the chat detail so a subsequent reload
 *  sees server truth. */
export function useUpdateAccumulatedGraph() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      chat_id,
      graph,
    }: {
      chat_id: string;
      graph: Record<string, unknown> | null;
    }) => updateAccumulatedGraph(chat_id, graph),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: chatKey(variables.chat_id) });
    },
  });
}
